import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TradingAccount, CreateAccountInput } from '@bini-trading/shared-types';
import { ApiError, api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';

const BROKER_OPTIONS = [
  { value: 'the5ers', label: 'The 5%ers' },
  { value: 'ftmo', label: 'FTMO' },
  { value: 'fundednext', label: 'FundedNext' },
  { value: 'topstep', label: 'TopStep' },
  { value: 'mff', label: 'MyForexFunds' },
  { value: 'other', label: 'Otro' },
] as const;

const KIND_OPTIONS = [
  { value: 'evaluation', label: 'Evaluación' },
  { value: 'funded', label: 'Fondeada' },
  { value: 'personal', label: 'Personal / propia' },
  { value: 'demo', label: 'Demo' },
  { value: 'copy_trader', label: 'Copy Trader' },
] as const;

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD · Dólar' },
  { value: 'EUR', label: 'EUR · Euro' },
  { value: 'GBP', label: 'GBP · Libra' },
  { value: 'CLP', label: 'CLP · Peso chileno' },
] as const;

const KIND_LABEL: Record<string, string> = Object.fromEntries(
  KIND_OPTIONS.map((o) => [o.value, o.label]),
);
const BROKER_LABEL: Record<string, string> = Object.fromEntries(
  BROKER_OPTIONS.map((o) => [o.value, o.label]),
);

const KIND_TONE: Record<string, string> = {
  evaluation: 'text-warn-DEFAULT bg-warn-DEFAULT/10 border-warn-DEFAULT/30',
  funded: 'text-win-DEFAULT bg-win-DEFAULT/10 border-win-DEFAULT/30',
  personal: 'text-info-DEFAULT bg-info-DEFAULT/10 border-info-DEFAULT/30',
  demo: 'text-ink-muted bg-surface-panel border-surface-border',
  copy_trader: 'text-brand-500 bg-brand-500/10 border-brand-500/30',
};

const fmtCurrency = (amount: number, currency: string): string =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 }).format(
    amount,
  );

export default function Accounts(): ReactElement {
  const qc = useQueryClient();
  const [editTarget, setEditTarget] = useState<TradingAccount | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TradingAccount | null>(null);

  const { data: accounts, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api<TradingAccount[]>('/accounts'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      setDeleteTarget(null);
    },
  });

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs text-ink-subtle tracking-[0.18em] uppercase mb-2">
              Cuentas de fondeo
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Tus cuentas</h1>
            <p className="text-ink-muted mt-2">
              Cada cuenta de fondeo se administra por separado: trades, P&L y analytics independientes.
            </p>
          </div>
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            + Nueva cuenta
          </Button>
        </div>

        {isLoading ? (
          <div className="text-ink-subtle text-sm font-mono py-12 text-center">Cargando…</div>
        ) : !accounts || accounts.length === 0 ? (
          <EmptyState onCreate={() => setShowCreate(true)} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((acc) => (
              <AccountCard
                key={acc.id}
                account={acc}
                onEdit={() => setEditTarget(acc)}
                onDelete={() => setDeleteTarget(acc)}
              />
            ))}
          </div>
        )}
      </div>

      {(showCreate || editTarget) && (
        <AccountFormModal
          target={editTarget}
          onClose={() => {
            setShowCreate(false);
            setEditTarget(null);
          }}
        />
      )}

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar cuenta"
        description={
          deleteTarget
            ? `Vas a eliminar "${deleteTarget.name}". Todos los trades vinculados se borrarán también.`
            : ''
        }
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              Sí, eliminar
            </Button>
          </div>
        }
      >
        <p className="text-sm text-ink-muted">Esta acción no se puede deshacer.</p>
      </Modal>
    </AppLayout>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): ReactElement {
  return (
    <div className="rounded-2xl border-2 border-dashed border-surface-border bg-surface-elevated/30 p-12 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-brand-500/10 border border-brand-500/30 flex items-center justify-center text-brand-500 text-2xl">
        ◈
      </div>
      <h3 className="text-lg font-semibold text-ink mb-1">Aún no tenés cuentas</h3>
      <p className="text-sm text-ink-muted mb-6 max-w-md mx-auto">
        Empezá registrando tu primera cuenta de fondeo. Por ejemplo: <em>The5ers Eval $10K</em>.
      </p>
      <Button variant="primary" onClick={onCreate}>
        + Crear primera cuenta
      </Button>
    </div>
  );
}

function AccountCard({
  account,
  onEdit,
  onDelete,
}: {
  account: TradingAccount;
  onEdit: () => void;
  onDelete: () => void;
}): ReactElement {
  const navigate = useNavigate();
  const stats = account.stats;
  const pnl = stats?.totalPnl ?? 0;
  const balance = stats?.currentBalance ?? account.initialBalance;
  const pnlPct = account.initialBalance > 0 ? pnl / account.initialBalance : 0;
  const pnlColor = pnl > 0 ? 'text-win-DEFAULT' : pnl < 0 ? 'text-loss-DEFAULT' : 'text-ink-muted';

  // Click en cualquier parte de la card → ir al calendario filtrado por cuenta.
  // Excepto en los botones de footer (stopPropagation evita que disparen).
  const goToJournal = (): void => {
    navigate(`/calendar?accountId=${account.id}`);
  };

  return (
    <div
      onClick={goToJournal}
      className="group rounded-xl border border-surface-border bg-surface-elevated p-5 hover:border-brand-500/50 hover:shadow-lg hover:shadow-brand-500/5 transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-ink truncate group-hover:text-brand-500 transition-colors">
            {account.name}
          </div>
          <div className="text-xs text-ink-subtle mt-0.5">
            {BROKER_LABEL[account.broker] ?? account.broker}
          </div>
        </div>
        <span
          className={`text-[10px] uppercase tracking-wider font-semibold border px-2 py-0.5 rounded whitespace-nowrap ${
            KIND_TONE[account.kind] ?? 'border-surface-border'
          }`}
        >
          {KIND_LABEL[account.kind] ?? account.kind}
        </span>
      </div>

      {/* Balance actual prominente */}
      <div className="font-mono text-2xl font-bold text-ink leading-tight">
        {fmtCurrency(balance, account.currency)}
      </div>
      <div className="flex items-baseline gap-2 mt-1 text-xs">
        <span className="text-ink-subtle">
          inicial {fmtCurrency(account.initialBalance, account.currency)}
        </span>
        {stats && stats.tradesClosed > 0 && (
          <span className={`font-mono font-semibold ${pnlColor}`}>
            {pnl >= 0 ? '+' : ''}
            {pnl.toLocaleString('es-CL', { maximumFractionDigits: 2 })}
            {pnlPct !== 0 && (
              <span className="ml-1 text-[10px] opacity-80">
                ({pnlPct >= 0 ? '+' : ''}
                {(pnlPct * 100).toFixed(2)}%)
              </span>
            )}
          </span>
        )}
      </div>

      {/* Stats row — solo si hay actividad */}
      {stats && stats.tradesTotal > 0 ? (
        <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-surface-border">
          <Stat
            label="Trades"
            value={String(stats.tradesTotal)}
            sub={stats.tradesOpen > 0 ? `${stats.tradesOpen} abiertos` : undefined}
          />
          <Stat
            label="Wins"
            value={String(stats.wins)}
            sub={stats.tradesClosed > 0 ? `${stats.losses}L` : undefined}
            tone={stats.wins > 0 ? 'win' : undefined}
          />
          <Stat
            label="Winrate"
            value={
              stats.tradesClosed > 0
                ? `${(stats.winrate * 100).toFixed(0)}%`
                : '—'
            }
          />
        </div>
      ) : (
        <div className="mt-4 pt-4 border-t border-surface-border text-xs text-ink-faint italic">
          Sin trades registrados aún.
        </div>
      )}

      {account.notes && (
        <p className="text-[11px] text-ink-muted mt-3 leading-relaxed line-clamp-2 italic">
          {account.notes}
        </p>
      )}

      {!account.active && (
        <div className="mt-3 text-[10px] uppercase tracking-wider text-ink-subtle font-semibold">
          ◌ Inactiva
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-surface-border flex justify-between items-center">
        <span className="text-[11px] text-brand-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          Ver journal →
        </span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            ✎
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            ✕
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'win' | 'loss';
}): ReactElement {
  const valueClass =
    tone === 'win' ? 'text-win-DEFAULT' : tone === 'loss' ? 'text-loss-DEFAULT' : 'text-ink';
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-ink-subtle font-semibold">
        {label}
      </div>
      <div className={`mt-0.5 font-mono text-sm font-bold ${valueClass}`}>{value}</div>
      {sub && <div className="text-[9px] text-ink-faint">{sub}</div>}
    </div>
  );
}

function AccountFormModal({
  target,
  onClose,
}: {
  target: TradingAccount | null;
  onClose: () => void;
}): ReactElement {
  const qc = useQueryClient();
  const isEdit = !!target;

  const [name, setName] = useState(target?.name ?? '');
  const [broker, setBroker] = useState<string>(target?.broker ?? 'the5ers');
  const [kind, setKind] = useState<string>(target?.kind ?? 'evaluation');
  const [initialBalance, setInitialBalance] = useState<string>(
    target ? String(target.initialBalance) : '',
  );
  const [currency, setCurrency] = useState<string>(target?.currency ?? 'USD');
  const [notes, setNotes] = useState(target?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (input: CreateAccountInput) => {
      if (target) {
        return api<TradingAccount>(`/accounts/${target.id}`, {
          method: 'PATCH',
          body: input,
        });
      }
      return api<TradingAccount>('/accounts', { method: 'POST', body: input });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message);
      else setError('Error inesperado');
    },
  });

  const submit = (): void => {
    setError(null);
    const balance = Number(initialBalance);
    if (!name.trim()) return setError('El nombre es requerido');
    if (!Number.isFinite(balance) || balance <= 0) {
      return setError('Balance debe ser un número mayor a 0');
    }
    mutation.mutate({
      name: name.trim(),
      broker: broker as CreateAccountInput['broker'],
      kind: kind as CreateAccountInput['kind'],
      initialBalance: balance,
      currency,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Editar cuenta' : 'Nueva cuenta de fondeo'}
      description={
        isEdit
          ? 'Modificá los datos. Los trades vinculados no se ven afectados.'
          : 'Una cuenta = un programa de fondeo o cuenta propia. Trades y analytics se aíslan por cuenta.'
      }
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" loading={mutation.isPending} onClick={submit}>
            {isEdit ? 'Guardar cambios' : 'Crear cuenta'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          label="Nombre"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej: The5ers Eval $10K"
          autoFocus
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Broker / proveedor"
            options={BROKER_OPTIONS}
            value={broker}
            onChange={setBroker}
          />
          <Select label="Tipo" options={KIND_OPTIONS} value={kind} onChange={setKind} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <Input
              label="Balance inicial"
              type="number"
              step="0.01"
              min="0"
              value={initialBalance}
              onChange={(e) => setInitialBalance(e.target.value)}
              placeholder="10000"
            />
          </div>
          <Select label="Moneda" options={CURRENCY_OPTIONS} value={currency} onChange={setCurrency} />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
            Notas (opcional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Ej: Reglas de la fase 1, target 8%, drawdown máx 5%..."
            className="w-full px-3 py-2 rounded-lg bg-surface-elevated border border-surface-border text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500 transition-colors resize-none"
          />
        </div>

        {error && (
          <div className="p-3 rounded-lg border border-loss/30 bg-loss/10 text-sm text-loss">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
